import { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import './DataGrid.css';

// AG Grid v33+: register the free (community) features once.
ModuleRegistry.registerModules([AllCommunityModule]);

// Light indigo look matching the rest of the admin panel.
const gridTheme = themeQuartz.withParams({
  accentColor: '#4f46e5',
  fontFamily: 'inherit',
  fontSize: 14,
  headerFontWeight: 600,
  headerBackgroundColor: '#f8fafc',
  headerTextColor: '#334155',
  foregroundColor: '#0f172a',
  borderColor: '#e2e8f0',
  wrapperBorderRadius: 10,
  spacing: 7,
});

const DEFAULT_COL = {
  sortable: true,
  filter: true,
  resizable: true,
  flex: 1,
  minWidth: 110,
};

/** Text for empty cells. */
export const dash = (value) => (value === null || value === undefined || value === '' ? '—' : value);

/**
 * Right-aligned number column with a number filter. `format` turns the
 * value into text (e.g. fmtQty); `blankZero` leaves zeros empty.
 */
export function numberColumn(headerName, field, format, options = {}) {
  return {
    headerName,
    field,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    minWidth: 110,
    valueFormatter: (p) => {
      if (p.value === null || p.value === undefined || p.value === '') return options.empty ?? '';
      if (options.blankZero && Number(p.value) === 0) return '';
      return format ? format(p.value) : String(p.value);
    },
    ...options.colDef,
  };
}

/**
 * Column for row action buttons (Edit, Delete, ...): no sorting, filtering
 * or menu, pinned right. `render(row)` returns the buttons.
 * options: headerName, width, pinned (false = not pinned), className (extra
 * class on the buttons' wrapper, so a page's existing button styles apply).
 */
export function actionsColumn(render, options = {}) {
  return {
    headerName: options.headerName ?? 'Actions',
    colId: 'actions',
    sortable: false,
    filter: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    flex: 0,
    width: options.width ?? 170,
    minWidth: options.width ?? 170,
    pinned: options.pinned === false ? undefined : 'right',
    cellRenderer: (params) =>
      params.data ? (
        <div className={`data-grid__actions ${options.className || ''}`.trim()}>
          {render(params.data)}
        </div>
      ) : null,
  };
}

/**
 * Admin-panel table built on AG Grid (community): sorting, column filters,
 * resizing, paging and a quick search box.
 *
 * Props:
 *   rowData, columnDefs            as in AG Grid
 *   getRowId(row)                  stable id (defaults to row.id; null = rows
 *                                  have no unique key, AG Grid numbers them)
 *   search                         show the quick search box (default true)
 *   searchPlaceholder
 *   toolbar                        extra elements shown next to the search box
 *   pageSize                       rows per page (default 20; 0 = no paging)
 *   emptyMessage                   shown when there are no rows
 *   rowHeight, autoHeight          see AG Grid
 *   gridProps                      any other AG Grid props
 */
function DataGrid({
  rowData,
  columnDefs,
  getRowId,
  search = true,
  searchPlaceholder = 'Search…',
  toolbar = null,
  pageSize = 20,
  emptyMessage = 'Nothing to show.',
  className = '',
  gridProps = {},
}) {
  const [quickFilter, setQuickFilter] = useState('');
  const [shown, setShown] = useState(null);
  const rows = rowData || [];

  // AG Grid reads getRowId once, so keep one stable function that calls the
  // latest prop.
  const idRef = useRef(getRowId);
  idRef.current = getRowId;
  const rowId = useCallback(
    (params) => String(idRef.current ? idRef.current(params.data) : params.data.id),
    [],
  );

  return (
    <div className={`data-grid ${className}`.trim()}>
      {search || toolbar ? (
        <div className="data-grid__toolbar">
          {search ? (
            <input
              type="search"
              className="data-grid__search"
              value={quickFilter}
              onChange={(event) => setQuickFilter(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          ) : null}
          {toolbar}
          <span className="data-grid__count">
            {shown !== null && shown !== rows.length
              ? `${shown} of ${rows.length} rows`
              : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`}
          </span>
        </div>
      ) : null}
      <AgGridReact
        theme={gridTheme}
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL}
        getRowId={getRowId === null ? undefined : rowId}
        quickFilterText={quickFilter}
        domLayout="autoHeight"
        pagination={pageSize > 0}
        paginationPageSize={pageSize || undefined}
        paginationPageSizeSelector={pageSize > 0 ? [10, 20, 50, 100] : undefined}
        overlayNoRowsTemplate={`<span class="data-grid__empty">${emptyMessage}</span>`}
        suppressCellFocus
        enableCellTextSelection
        onModelUpdated={(event) => setShown(event.api.getDisplayedRowCount())}
        {...gridProps}
      />
    </div>
  );
}

export default DataGrid;
